import {
  useState,
  useEffect,
  useRef,
  KeyboardEvent,
  ChangeEvent,
  ActionDispatch,
  Dispatch,
  SetStateAction,
} from 'react';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import { constants, notify } from '../index';
import { useOnClickOutside } from '../hooks/index';

type TagsDropdownProps = {
  initialTags: string[];
  formState: any;
  setIsDirty?: Dispatch<SetStateAction<boolean>>;
  setFormState: ActionDispatch<[state: Partial<any>]>;
};

export default function TagsDropdown({
  initialTags,
  formState,
  setIsDirty,
  setFormState,
}: TagsDropdownProps) {
  const autoCompleteRef = useRef<HTMLElement>(null);
  const [autoCompleteValue, setAutoCompleteValue] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    setFormState({ ...formState, memberTags: initialTags ?? [] });
    setAutoCompleteValue(initialTags ?? []);
  }, [initialTags]);

  const addTag = (raw: string) => {
    const newTag = raw.trim();
    if (!newTag) {
      notify('Please enter a value for the new division code', 'error');
      return;
    }
    if (!newTag.match(constants.REGEX.TAG)) {
      const message =
        'Division codes must be between 1 and 35 characters.\nDivision codes cannot include commas';
      notify(message, 'error');
      return;
    }
    if (autoCompleteValue.some((item) => item === newTag)) return;

    const updated = [...autoCompleteValue, newTag];
    setAutoCompleteValue(updated);
    setInputValue('');
    setFormState({ ...formState, memberTags: updated });
    if (setIsDirty) setIsDirty(true);
  };

  useOnClickOutside(autoCompleteRef, () => {
    if (inputValue) {
      const trimmed = inputValue.trim();
      if (trimmed) {
        addTag(trimmed);
      }
    }
  });

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === 'Tab' || event.key === ',') {
      event.preventDefault();
      addTag(inputValue);
    }
  };

  return (
    <Stack spacing={0} flexGrow={1} alignItems="flex-start">
      <Stack
        sx={{
          marginLeft: 1.2,
          marginBottom: -1.1,
          zIndex: 1,
          backgroundColor: 'white',
          paddingLeft: 0.6,
          paddingRight: 0.6,
        }}
      >
        <Typography sx={{ fontSize: 12, color: '#777' }}>Division Codes</Typography>
      </Stack>
      <Stack spacing={1} alignItems="center" flexWrap="wrap" width="100%">
        <Box
          ref={autoCompleteRef}
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            minHeight: 56,
            px: 1,
            py: 0.5,
            border: '1px solid rgba(0, 0, 0, 0.23)',
            borderRadius: 1,
            bgcolor: 'background.paper',
            width: '100%',
            paddingTop: 1,
            '&:hover': { borderColor: 'black' },
          }}
        >
          {autoCompleteValue?.map((option, index) => (
            <Chip
              key={index}
              label={option}
              onDelete={() => {
                const updated = autoCompleteValue.filter((_, i) => i !== index);
                setFormState({ ...formState, memberTags: updated });
                setAutoCompleteValue(updated);
                if (setIsDirty) setIsDirty(true);
              }}
              sx={{
                m: 0.25,
                bgcolor: '#d0e3d4aa',
                fontWeight: 600,
                borderWidth: 1,
                borderStyle: 'solid',
                borderColor: 'primary.main',
                color: '#000000aa',
                '& .MuiChip-deleteIcon': {
                  color: 'primary.main',
                  '&:hover': { color: '#4b847fff' },
                },
                '&.Mui-disabled': {
                  borderWidth: 0,
                  opacity: 1,
                  bgcolor: '#00000014',
                  color: '#000000cc',
                  fontWeight: 500,
                },
              }}
            />
          ))}
          <TextField
            variant="standard"
            value={inputValue}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Enter codes here (Press enter or tab key to add multiple codes)"
            slotProps={{
              input: { disableUnderline: true },
              htmlInput: { maxLength: constants.TAG_MAXLENGTH },
            }}
            sx={{ flexGrow: 1, minWidth: 120 }}
          />
        </Box>
      </Stack>
    </Stack>
  );
}
